import {
  Row,
  Col,
  Typography,
  Table,
  Button,
  message,
  Descriptions,
} from "antd";
import { routes } from "common/routes";
import { generateHeaders, getSlotName } from "common/utils";
import { AppPageHeader } from "components/AppPageHeader";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useDocumentTitle } from "hooks/usePageTitle";
import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  cancelProcessingRequisition,
  removeProcessingRequisition,
  saveRequisition,
} from "store/requisition/requisitionSlice";

const MESSAGE_KEY = "SAVE_REQUISITION";

export const SaveStoreRequisitionPage = () => {
  useDocumentTitle("Save Requisition");
  const { processingRequisition, status, error, slots } = useAppSelector(
    (state) => state.requisition
  );
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const { timeUploaded } = processingRequisition;
    if (timeUploaded) {
      const uploadedTime = new Date(timeUploaded).getTime();
      const currentTime = new Date().getTime();
      const minsPassed = (currentTime - uploadedTime) / 1000 / 60;

      if (minsPassed >= 15) {
        dispatch(removeProcessingRequisition());
      }
    }

    if (!Object.keys(processingRequisition).length) {
      navigate(`/${routes.STORE_REQUISITION.UPLOAD}`);
    }
  }, [dispatch, processingRequisition, navigate]);

  useEffect(() => {
    if (status === "loading") {
      message.loading({ content: "saving requisition", key: MESSAGE_KEY });
    } else if (status === "success") {
      message.success({
        content: "Requisition saved!",
        duration: 5,
        key: MESSAGE_KEY,
      });
    } else if (status === "cancelling") {
      message.loading({ content: "cancelling requisition", key: MESSAGE_KEY });
    } else if (status === "error") {
      message.error({ content: error, key: MESSAGE_KEY });
    }

    return () => {
      message.destroy();
    };
  }, [status, error]);

  let content;
  const { headerMapping } = processingRequisition;
  const { Text } = Typography;

  const generateMemoizedHeaders = useMemo(
    () => generateHeaders(headerMapping),
    [headerMapping]
  );

  const onSaveRequisition = () => {
    dispatch(saveRequisition());
  };

  const onCancelSave = () => {
    dispatch(cancelProcessingRequisition());
  };

  if (!Object.keys(processingRequisition).length) {
    content = <Text>Nothing to show here!</Text>;
  } else {
    const headers = generateMemoizedHeaders || {};
    const successRows = processingRequisition.successRows;
    const data = successRows?.map((row, i) => ({ ...row, key: `data-${i}` }));
    const columns = [
      ...Object.keys(headers).map(
        (key) =>
          ({
            key,
            title: headers[key],
            dataIndex: key,
          } as any)
      ),
    ];
    content = (
      <Table
        size="small"
        columns={columns}
        dataSource={data}
        scroll={{ y: 500, x: "100vh" }}
        summary={() => (
          <Table.Summary fixed="bottom">
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={24}>
                <Row>
                  <Col>
                    <Button
                      type="primary"
                      onClick={onSaveRequisition}
                      loading={status === "loading"}
                      disabled={status === "loading"}
                    >
                      Save Requisition
                    </Button>
                  </Col>
                  <Col style={{ justifyContent: "left", marginLeft: "20px" }}>
                    <Button
                      onClick={onCancelSave}
                      disabled={status === "loading"}
                    >
                      Cancel
                    </Button>
                  </Col>
                </Row>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        )}
      ></Table>
    );
  }

  return (
    <>
      <Row>
        <Col>
          <AppPageHeader title="Save Store Requisition" />
        </Col>
      </Row>
      <Row style={{ marginBottom: "1rem" }}>
        {!!(slots && processingRequisition.slot) && (
          <Descriptions
            size="small"
            layout="vertical"
            labelStyle={{ fontWeight: "bold" }}
            bordered
            column={{ xs: 2 }}
          >
            <Descriptions.Item label="Date">
              {processingRequisition.date}
            </Descriptions.Item>
            <Descriptions.Item label="Slot">
              {getSlotName(slots, processingRequisition.slot)}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Row>
      <Row>
        <Col xs={24}>{content}</Col>
      </Row>
    </>
  );
};
